"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.phoneNumberOptionalSchema = exports.createPhoneNumberFormFields = exports.phoneNumberFormKey = exports.phoneNumberSchema = exports.phoneNumberFormFields = void 0;
const zod_1 = require("zod");
const regexp_1 = require("@Const/regexp");
const error_messages_1 = require("@Const/error-messages");
const error_messages_2 = require("@Entities/schemas/work/error-messages");
const form_field_types_1 = require("@App/enum/form-field-types");
const populate_schema_1 = __importDefault(require("@App/forms/v2/helpers/populate-schema"));
const utils_1 = require("@App/common/utils");
const phoneCountryCodeKey = 'phoneCountryCode';
const phoneAreaCodeKey = 'phoneAreaCode';
const phoneNumberKey = 'phoneNumber';
const phoneCountryCodeLabel = 'Country';
const phoneAreaCodeLabel = 'Area Code';
const phoneNumberLabel = 'Phone Number';
const phoneNumberFormKey = 'phoneNumber';
exports.phoneNumberFormKey = phoneNumberFormKey;
const phoneNumberSchema = zod_1.z
    .object({
    [phoneCountryCodeKey]: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).pipe(zod_1.z.coerce.string({
        required_error: error_messages_1.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_1.REQUIRED_MESSAGE,
    })),
    [phoneAreaCodeKey]: zod_1.z.union([
        zod_1.z.literal(''),
        zod_1.z
            .string()
            .regex(regexp_1.ONLY_NUMBERS_REGEX, error_messages_1.IS_NUMBER_MESSAGE)
            .max(10, (0, error_messages_2.stringSizeMaxMessage)(phoneAreaCodeLabel, 10))
            .optional(),
    ]),
    [phoneNumberKey]: zod_1.z
        .string({
        required_error: error_messages_1.REQUIRED_MESSAGE,
    })
        .regex(regexp_1.ONLY_NUMBERS_REGEX, error_messages_1.IS_NUMBER_MESSAGE)
        .max(40, (0, error_messages_2.stringSizeMaxMessage)(phoneNumberLabel, 40)),
})
    .superRefine((phone, ctx) => {
    if ((0, utils_1.isPhoneCodeUS)(phone.phoneCountryCode)) {
        if (!phone.phoneAreaCode) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: error_messages_1.REQUIRED_MESSAGE,
                path: [phoneAreaCodeKey],
            });
        }
        else if (phone.phoneAreaCode.length !== 3) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `${error_messages_1.STATIC_LENGTH_MESSAGE} 3`,
                path: [phoneAreaCodeKey],
            });
        }
        if (phone.phoneNumber.length !== 7) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `${error_messages_1.STATIC_LENGTH_MESSAGE} 7`,
                path: [phoneNumberKey],
            });
        }
    }
});
exports.phoneNumberSchema = phoneNumberSchema;
const phoneNumberOptionalSchema = zod_1.z
    .object({
    [phoneCountryCodeKey]: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]).pipe(zod_1.z.coerce
        .string({
        required_error: error_messages_1.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_1.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_1.REQUIRED_MESSAGE)
        .optional()),
    [phoneAreaCodeKey]: zod_1.z.union([
        zod_1.z.literal('', {
            errorMap: () => ({ message: error_messages_1.REQUIRED_MESSAGE }),
        }),
        zod_1.z
            .string({ invalid_type_error: error_messages_1.REQUIRED_MESSAGE })
            .min(1, (0, error_messages_2.stringSizeMinMessage)(phoneAreaCodeLabel, 1))
            .regex(regexp_1.ONLY_NUMBERS_REGEX, error_messages_1.IS_NUMBER_MESSAGE)
            .max(10, (0, error_messages_2.stringSizeMaxMessage)(phoneAreaCodeLabel, 10))
            .optional(),
    ]),
    [phoneNumberKey]: zod_1.z
        .string({
        required_error: error_messages_1.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_1.REQUIRED_MESSAGE,
    })
        .min(6, (0, error_messages_2.stringSizeMinMessage)(phoneNumberLabel, 6))
        .regex(regexp_1.ONLY_NUMBERS_REGEX, error_messages_1.IS_NUMBER_MESSAGE)
        .max(40, (0, error_messages_2.stringSizeMaxMessage)(phoneNumberLabel, 40))
        .optional(),
})
    .superRefine((phone, ctx) => {
    const { phoneCountryCode, phoneAreaCode, phoneNumber } = phone;
    if (phoneCountryCode || phoneAreaCode || phoneNumber) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: error_messages_1.REQUIRED_MESSAGE,
            path: [phoneCountryCodeKey, phoneNumberKey],
        });
        if ((0, utils_1.isPhoneCodeUS)(phoneCountryCode)) {
            if (!phoneAreaCode) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    message: error_messages_1.REQUIRED_MESSAGE,
                    path: [phoneAreaCodeKey],
                });
            }
            else if (phoneAreaCode.length !== 3) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    message: `${error_messages_1.STATIC_LENGTH_MESSAGE} 3`,
                    path: [phoneAreaCodeKey],
                });
            }
            if (phoneNumber && phoneNumber.length !== 7) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    message: `${error_messages_1.STATIC_LENGTH_MESSAGE} 7`,
                    path: [phoneNumberKey],
                });
            }
        }
    }
});
exports.phoneNumberOptionalSchema = phoneNumberOptionalSchema;
const createPhoneNumberFormFields = (parentObjectName) => {
    const phoneNumberFormFieldsNoSchema = {
        [phoneCountryCodeKey]: {
            name: phoneCountryCodeKey,
            parentObjectName: parentObjectName,
            label: phoneCountryCodeLabel,
            type: form_field_types_1.FormFieldTypes.DROPDOWN,
            options: [],
            className: 'phone-code-form-type border',
            showLabelOnSelect: false,
            isPhoneType: true,
            assistiveTextFunction: (required) => (required ? 'Country Required' : ''),
            schema: zod_1.z.number(),
            isClearable: true,
        },
        [phoneAreaCodeKey]: {
            name: phoneAreaCodeKey,
            parentObjectName: parentObjectName,
            label: phoneAreaCodeLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
            assistiveTextFunction: (required) => (required ? 'Area Required' : 'Area'),
        },
        [phoneNumberKey]: {
            name: phoneNumberKey,
            parentObjectName: parentObjectName,
            label: phoneNumberLabel,
            assistiveTextFunction: (required) => (required ? 'Number Required' : 'Number'),
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
    };
    return (0, populate_schema_1.default)(phoneNumberFormFieldsNoSchema, phoneNumberSchema);
};
exports.createPhoneNumberFormFields = createPhoneNumberFormFields;
const phoneNumberFormFieldsNoSchema = createPhoneNumberFormFields(phoneNumberFormKey);
const phoneNumberFormFields = (0, populate_schema_1.default)(phoneNumberFormFieldsNoSchema, phoneNumberSchema);
exports.phoneNumberFormFields = phoneNumberFormFields;
